import styled from '@emotion/styled';
import _ from 'lodash';
import PropTypes from 'prop-types';

import AcousticResultsTable from './AcousticResultsTable';
import {
  getDocTitle,
  getDocSize,
  getDocDescription,
  getAssemblyResults,
  isCoveredByGuidePdf,
  resultCountLabel,
  splitAssemblyAttributes,
} from './acousticSelectorHelpers';
import pathToContent from './path_to_content';
import Theme from '../../../../../themes/index';
import RichText from '../../../../OC/PageBuilder/RichText';
import Icon from '../../../../OC/oc-icon';
import { openComponentModal } from '../../../../PageBuilder/helpers/modal';

const ModalContent = styled.div`
  display: flex;
  gap: 32px;
  max-width: 1218px;
  margin: 0 auto;
  padding: 16px;

  @media (max-width: 1250px) {
    max-width: calc(100vw - 32px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 8px;
  }
`;

const ImageContainer = styled.div`
  flex-shrink: 0;
  width: 300px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 1100px) {
    width: 500px;
  }

  @media (min-width: 900px) and (max-width: 1099px) {
    width: 400px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }

  img {
    width: auto;
    max-width: 100%;
    height: auto;
    object-fit: contain;
  }
`;

const DetailsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const Header = styled.div`
  border-bottom: 1px solid #ddd;
  padding-bottom: 16px;
`;

const Label = styled.div`
  color: ${Theme.colors.textGrey};
  font-size: 12px;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const AssemblyNumber = styled.h2`
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
`;

const ResultCount = styled.div`
  color: ${Theme.colors.textGrey};
  font-size: 14px;
  margin-top: 4px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 16px;
  line-height: 1.5;

  strong {
    font-weight: 600;
  }
`;

const AssemblyDetailsSection = styled.div`
  margin-top: 8px;
`;

const AssemblyDetailsHeading = styled.h3`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 12px 0;
`;

const AssemblyDetailsText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const DownloadButton = styled.a`
  align-items: center;
  background-color: ${Theme.colors.brand};
  border: none;
  color: white;
  cursor: pointer;
  display: inline-flex;
  font-size: 16px;
  font-weight: 600;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 24px;
  text-decoration: none;
  transition: background-color 0.2s;
  width: fit-content;

  &:hover {
    background-color: #b50c67;
    color: white;
  }

  &:focus {
    outline: 2px solid ${Theme.colors.brand};
    outline-offset: 2px;
  }

  svg {
    fill: currentColor;
  }
`;

const FileSize = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

const Description = styled.div`
  & > div > div {
    margin: 0;
  }

  p {
    margin: 8px 0;
  }
`;

const AcousticSelectorModal = ({ result, attributes }) => {
  const assemblyNumber = getDocTitle(result);
  const thumbnailUrl = result?.contents?.thumbnail;
  const pdfUrl = pathToContent(result);
  const pdfSize = getDocSize(result);
  const shortDescription = getDocDescription(result);
  const assemblyResults = getAssemblyResults(result);
  const { rowAttributes, cardAttributes } = splitAssemblyAttributes(attributes, assemblyResults);

  const infoItems = cardAttributes
    .map(attribute => {
      const { label, field, type, of } = attribute;

      if (!label) {
        return null;
      }

      const resultValue = _.get(result, field);
      const wrappedValue = Array.isArray(resultValue) ? resultValue : [resultValue];
      switch (type) {
        case 'choices':
          return {
            label,
            value: _.sortBy(wrappedValue, a => {
              const keys = Object.keys(of || {});
              return keys.indexOf(a);
            }).map(v => of?.[v]?.label || v),
          };
        default:
          return { label, value: resultValue?.toString() };
      }
    })
    .filter(x => x && x.value && x.value.length > 0);

  const descriptionItem = infoItems.find(
    item => item.label.toLowerCase() === 'description' || item.label.toLowerCase() === 'assembly details'
  );
  const otherItems = infoItems.filter(
    item => item.label.toLowerCase() !== 'description' && item.label.toLowerCase() !== 'assembly details'
  );

  return (
    <ModalContent>
      {thumbnailUrl && (
        <ImageContainer>
          <img src={thumbnailUrl} alt={assemblyNumber} />
        </ImageContainer>
      )}

      <DetailsContainer>
        {assemblyNumber && (
          <Header>
            <Label>ASSEMBLY NUMBER</Label>
            <AssemblyNumber>{assemblyNumber}</AssemblyNumber>
            {assemblyResults.length > 0 && <ResultCount>{resultCountLabel(assemblyResults.length)}</ResultCount>}
          </Header>
        )}

        {rowAttributes.length > 0 && <AcousticResultsTable results={assemblyResults} rowAttributes={rowAttributes} />}

        {result.showDescription && shortDescription && (
          <Description>
            <RichText content={shortDescription} />
          </Description>
        )}

        {otherItems.map(item => (
          <DetailRow key={item.label}>
            <strong>{item.label}:</strong> {Array.isArray(item.value) ? item.value.join(', ') : item.value}
          </DetailRow>
        ))}

        {descriptionItem && (
          <AssemblyDetailsSection>
            <AssemblyDetailsHeading>Assembly Details</AssemblyDetailsHeading>
            <AssemblyDetailsText>
              {Array.isArray(descriptionItem.value) ? descriptionItem.value.join(', ') : descriptionItem.value}
            </AssemblyDetailsText>
          </AssemblyDetailsSection>
        )}

        {pdfUrl && isCoveredByGuidePdf(result) && (
          <DownloadButton
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-track="file-download"
            data-track-doc-type="Acoustic Selector PDF"
            data-track-assembly-number={assemblyNumber}
          >
            <Icon type="file-pdf-o" />
            Download PDF {pdfSize && <FileSize>{pdfSize}</FileSize>}
          </DownloadButton>
        )}
      </DetailsContainer>
    </ModalContent>
  );
};

AcousticSelectorModal.propTypes = {
  result: PropTypes.shape({
    metadata: PropTypes.shape({
      settings: PropTypes.shape({
        attributes: PropTypes.shape({
          acoustic_selector_attributes: PropTypes.shape({
            assembly_number: PropTypes.string,
          }),
        }),
      }),
    }),
    contents: PropTypes.shape({
      thumbnail: PropTypes.string,
      type: PropTypes.string,
      dms: PropTypes.shape({
        size_bytes: PropTypes.number,
      }),
      document: PropTypes.shape({
        size_bytes: PropTypes.number,
      }),
    }),
    name: PropTypes.string,
    showDescription: PropTypes.bool,
  }).isRequired,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      field: PropTypes.string,
      type: PropTypes.string,
    })
  ),
};

AcousticSelectorModal.defaultProps = {
  attributes: [],
};

export const openAcousticSelectorModal = ({ result, attributes }) => {
  openComponentModal(() => <AcousticSelectorModal result={result} attributes={attributes} />, '', { size: 'lg' });
};

export default AcousticSelectorModal;
